import { useMemo } from 'react';
import { useCookies } from 'react-cookie';

const MSC_PIPELINE_ID = 'msc_pipeline_id';
const SELECTION_PROCESS_ID = 'selection_process_id';
const CLIENT_ID = 'client_id';
const ACTUAL_STATE = 'actual_state';
const CANDIDATE_APPLICATION_ID = 'candidate_application_id';
const PROCESS_DATE_INFO = 'process_date_info';
const PROCESS_INFO = 'process_info';
const STATE_MACHINE_VERSION = 'state_machine_version';
const SCORE_DISAPPROVED_GUARANTOR_ID = 'score_disapproved_guarantor_id';

export type processDateType = {
  limitDateAssignatureContract: string;
  limitDateFinancingRequest: string;
  processDueDate: string;
};

export type processInfoType = {
  processName: string;
  processNameAbbreviation: string;
};

export type UseClientTypes = {
  clientId: string;
  setClientId: (value: string) => void;
  removeClientId: () => void;
  mscPipelineId: string;
  setMscPipelineId: (value: string) => void;
  removeMscPipelineId: () => void;
  selectionProcessId: string;
  setSelectionProcessId: (value: string) => void;
  processInfo: processInfoType;
  setProcessInfo: (value: processInfoType) => void;
  removeProcessInfo: () => void;
  removeSelectionProcessId: () => void;
  currentState: string;
  setActualState: (value: string) => void;
  removeActualState: () => void;
  candidateApplicationId: string;
  setCandidateApplicationId: (value: string) => void;
  removeCandidateApplicationId: () => void;
  processDateInfo: processDateType;
  setProcessDateInfo: (value: processDateType) => void;
  removeProcessDateInfo: () => void;
  clearAll: () => void;
  stateMachineVersion: string;
  setStateMachineVersion: (value: string) => void;
  removeStateMachineVersion: () => void;
  scoreDisapprovedGuarantorId: string;
  setScoreDisapprovedGuarantorId: (value: string) => void;
  removeScoreDisapprovedGuarantorId: () => void;
};

function useClient(): UseClientTypes {
  const [cookies, setCookie, removeCookie] = useCookies([
    MSC_PIPELINE_ID,
    SELECTION_PROCESS_ID,
    CLIENT_ID,
    ACTUAL_STATE,
    CANDIDATE_APPLICATION_ID,
    PROCESS_DATE_INFO,
    PROCESS_INFO,
    STATE_MACHINE_VERSION,
    SCORE_DISAPPROVED_GUARANTOR_ID,
  ]);

  const clientId = useMemo(() => cookies?.[CLIENT_ID] || null, [cookies]);
  const setClientId = (value: string) => setCookie(CLIENT_ID, value);
  const removeClientId = () => removeCookie(CLIENT_ID);

  const mscPipelineId = useMemo(
    () => cookies?.[MSC_PIPELINE_ID] || null,
    [cookies],
  );
  const setMscPipelineId = (value: string) => setCookie(MSC_PIPELINE_ID, value);
  const removeMscPipelineId = () => removeCookie(MSC_PIPELINE_ID);

  const selectionProcessId = useMemo(
    () => cookies?.[SELECTION_PROCESS_ID] || null,
    [cookies],
  );
  const setSelectionProcessId = (value: string) =>
    setCookie(SELECTION_PROCESS_ID, value);
  const removeSelectionProcessId = () => removeCookie(SELECTION_PROCESS_ID);

  const processInfo = useMemo(() => cookies?.[PROCESS_INFO] || null, [cookies]);
  const setProcessInfo = (value: processInfoType) =>
    setCookie(PROCESS_INFO, value);
  const removeProcessInfo = () => removeCookie(PROCESS_INFO);

  const currentState = useMemo(
    () => cookies?.[ACTUAL_STATE] || null,
    [cookies],
  );
  const setActualState = (value: string) =>
    setCookie(ACTUAL_STATE, value, {
      path: '/',
    });
  const removeActualState = () => removeCookie(ACTUAL_STATE);

  const candidateApplicationId = useMemo(
    () => cookies?.[CANDIDATE_APPLICATION_ID] || null,
    [cookies],
  );
  const setCandidateApplicationId = (value: string) =>
    setCookie(CANDIDATE_APPLICATION_ID, value);
  const removeCandidateApplicationId = () =>
    removeCookie(CANDIDATE_APPLICATION_ID);

  const processDateInfo = useMemo(
    () => cookies?.[PROCESS_DATE_INFO] || null,
    [cookies],
  );
  const setProcessDateInfo = (value: processDateType) =>
    setCookie(PROCESS_DATE_INFO, value);
  const removeProcessDateInfo = () => removeCookie(PROCESS_DATE_INFO);

  const stateMachineVersion = useMemo(
    () => cookies?.state_machine_version || null,
    [cookies],
  );

  const setStateMachineVersion = (value: string) =>
    setCookie(STATE_MACHINE_VERSION, value);
  const removeStateMachineVersion = () => removeCookie(STATE_MACHINE_VERSION);

  const scoreDisapprovedGuarantorId = useMemo(
    () => cookies?.score_disapproved_guarantor_id || null,
    [cookies],
  );

  const setScoreDisapprovedGuarantorId = (value: string) =>
    setCookie(SCORE_DISAPPROVED_GUARANTOR_ID, value);
  const removeScoreDisapprovedGuarantorId = () =>
    removeCookie(SCORE_DISAPPROVED_GUARANTOR_ID);

  const clearAll = () => {
    removeCookie(MSC_PIPELINE_ID);
    removeCookie(SELECTION_PROCESS_ID);
    removeCookie(CLIENT_ID);
    removeCookie(ACTUAL_STATE);
    removeCookie(CANDIDATE_APPLICATION_ID);
    removeCookie(PROCESS_DATE_INFO);
    removeCookie(PROCESS_INFO);
    removeCookie(STATE_MACHINE_VERSION);
    removeCookie(SCORE_DISAPPROVED_GUARANTOR_ID);
  };

  return {
    clientId,
    setClientId,
    removeClientId,
    mscPipelineId,
    setMscPipelineId,
    removeMscPipelineId,
    selectionProcessId,
    setSelectionProcessId,
    removeSelectionProcessId,
    currentState,
    setActualState,
    removeActualState,
    candidateApplicationId,
    removeCandidateApplicationId,
    setCandidateApplicationId,
    processDateInfo,
    setProcessDateInfo,
    removeProcessDateInfo,
    clearAll,
    processInfo,
    setProcessInfo,
    removeProcessInfo,
    stateMachineVersion,
    setStateMachineVersion,
    removeStateMachineVersion,
    scoreDisapprovedGuarantorId,
    setScoreDisapprovedGuarantorId,
    removeScoreDisapprovedGuarantorId,
  };
}

export default useClient;
